/**
 * Google Analytics Event Tracking Service
 * Tracks user actions and identifies friction points across the application
 */

class AnalyticsService {
  /**
   * Track agent toggle events (enable/disable)
   * @param {string} agentId - Agent identifier
   * @param {string} agentName - Agent display name
   * @param {boolean} isActive - New active state
   * @param {string} reason - Reason for toggle (user_choice, downgrade_required, upgrade, etc.)
   */
  trackAgentToggle(agentId, agentName, isActive, reason = 'user_choice') {
    if (typeof window.gtag === 'undefined') return;

    window.gtag('event', 'agent_toggle', {
      agent_id: agentId,
      agent_name: agentName,
      action: isActive ? 'enable' : 'disable',
      reason: reason,
      page: window.location?.pathname,
      timestamp: new Date()?.toISOString()
    });
  }

  /**
   * Track subscription changes (upgrades, downgrades, cancellations)
   * @param {string} changeType - Type of change (upgrade, downgrade, cancel)
   * @param {string} fromPlan - Current plan tier
   * @param {string} toPlan - Target plan tier
   * @param {number} priceChange - Price difference
   */
  trackSubscriptionChange(changeType, fromPlan, toPlan, priceChange = 0) {
    if (typeof window.gtag === 'undefined') return;

    window.gtag('event', 'subscription_change', {
      change_type: changeType,
      from_plan: fromPlan,
      to_plan: toPlan,
      price_change: priceChange,
      page: window.location?.pathname,
      timestamp: new Date()?.toISOString()
    });
  }

  /**
   * Track downgrade confirmation flow with hesitation metrics
   * @param {string} step - Current step in flow (initiated, agent_selection, confirmation, completed, abandoned)
   * @param {number} agentsToDeactivate - Number of agents required to deactivate
   * @param {number} agentsSelected - Number of agents currently selected
   * @param {number} timeSpent - Time spent in seconds
   */
  trackDowngradeFlow(step, agentsToDeactivate, agentsSelected = 0, timeSpent = 0) {
    if (typeof window.gtag === 'undefined') return;

    const hesitationMetrics = {
      selection_progress: agentsToDeactivate > 0 ? (agentsSelected / agentsToDeactivate) * 100 : 0,
      time_spent_seconds: timeSpent,
      is_hesitating: timeSpent > 30 && agentsSelected < agentsToDeactivate
    };

    window.gtag('event', 'downgrade_flow', {
      step: step,
      agents_to_deactivate: agentsToDeactivate,
      agents_selected: agentsSelected,
      ...hesitationMetrics,
      page: window.location?.pathname,
      timestamp: new Date()?.toISOString()
    });
  }

  /**
   * Track error recovery attempts
   * @param {string} errorType - Type of error (agent_activation_failed, message_send_failed, payment_failed, etc.)
   * @param {string} errorMessage - Error message
   * @param {number} retryCount - Number of retry attempts
   * @param {boolean} recovered - Whether error was successfully recovered
   * @param {string} context - Additional context (agent_id, conversation_id, etc.)
   */
  trackErrorRecovery(errorType, errorMessage, retryCount = 0, recovered = false, context = '') {
    if (typeof window.gtag === 'undefined') return;

    window.gtag('event', 'error_recovery', {
      error_type: errorType,
      error_message: errorMessage?.substring(0, 100), // Limit message length
      retry_count: retryCount,
      recovered: recovered,
      context: context,
      page: window.location?.pathname,
      timestamp: new Date()?.toISOString()
    });
  }

  /**
   * Track user friction during critical workflows
   * @param {string} workflow - Workflow name (agent_management, billing_update, message_send, etc.)
   * @param {string} frictionPoint - Specific friction point (slow_loading, confusing_ui, error_encountered, etc.)
   * @param {number} severity - Severity level (1-5, where 5 is critical)
   * @param {object} metadata - Additional metadata
   */
  trackFrictionPoint(workflow, frictionPoint, severity = 3, metadata = {}) {
    if (typeof window.gtag === 'undefined') return;

    window.gtag('event', 'friction_point', {
      workflow: workflow,
      friction_point: frictionPoint,
      severity: severity,
      ...metadata,
      page: window.location?.pathname,
      timestamp: new Date()?.toISOString()
    });
  }

  /**
   * Track task creation events
   * @param {string} agentId - Agent handling the task
   * @param {string} taskType - Type of task created
   * @param {boolean} success - Whether task was created successfully
   */
  trackTaskCreation(agentId, taskType, success = true) {
    if (typeof window.gtag === 'undefined') return;

    window.gtag('event', 'task_creation', {
      agent_id: agentId,
      task_type: taskType,
      success: success,
      page: window.location?.pathname,
      timestamp: new Date()?.toISOString()
    });
  }

  /**
   * Track message send events
   * @param {string} agentId - Agent receiving the message
   * @param {string} priority - Message priority
   * @param {boolean} success - Whether message was sent successfully
   */
  trackMessageSend(agentId, priority, success = true) {
    if (typeof window.gtag === 'undefined') return;

    window.gtag('event', 'message_send', {
      agent_id: agentId,
      priority: priority,
      success: success,
      page: window.location?.pathname,
      timestamp: new Date()?.toISOString()
    });
  }

  /**
   * Track upgrade CTA clicks
   * @param {string} location - Where the CTA was clicked (dashboard_banner, agent_management, locked_feature, etc.)
   * @param {string} currentPlan - Current subscription plan
   */
  trackUpgradeCTA(location, currentPlan) {
    if (typeof window.gtag === 'undefined') return;

    window.gtag('event', 'upgrade_cta_click', {
      cta_location: location,
      current_plan: currentPlan,
      page: window.location?.pathname,
      timestamp: new Date()?.toISOString()
    });
  }
}

export const analyticsService = new AnalyticsService();
export default analyticsService;